import { ReactNode } from "react";

import { Group, NumberInput, NumberInputProps, rem, Stack, StackProps, Text } from "@mantine/core";

import { IconHeartFilled } from "@tabler/icons-react";

import {
  VITALS_UNIT_RIGHT_SECTION_WIDTH,
  VitalsUnitRightSection,
} from "@/encounters/components/VitalSigns/components/VitalsUnitRightSection";

/**
 * Shared vertical rhythm for vitals drawers. Gap is locked so encounter forms cannot drift.
 */
export function VitalsFormStack({ children, mt = rem(8), ...props }: StackProps) {
  return (
    <Stack {...props} mt={mt} gap="md">
      {children}
    </Stack>
  );
}

/**
 * Section heading plus fields (Blood Pressure, Height) using the same label size as NumberInput.
 */
export function VitalsLabeledSection({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap="xs">
      <Text fw={500} size="md">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

/** Side-by-side vitals inputs (systolic/diastolic, ft/in). */
export function VitalsPairedFields({ children }: { children: ReactNode }) {
  return (
    <Group grow align="flex-start">
      {children}
    </Group>
  );
}

/** NumberInput defaults shared by every vitals form: clamp, no negatives, unit-chip width. */
export function VitalsNumberInput({
  allowNegative = false,
  clampBehavior = "strict",
  rightSectionWidth = VITALS_UNIT_RIGHT_SECTION_WIDTH,
  ...props
}: NumberInputProps) {
  return (
    // Callers pass data-testid; this wrapper only supplies shared NumberInput defaults.
    // eslint-disable-next-line quilted/require-data-testid
    <NumberInput
      allowNegative={allowNegative}
      clampBehavior={clampBehavior}
      rightSectionWidth={rightSectionWidth}
      {...props}
    />
  );
}

/** Heart Rate input with the shared icon and bpm chip. Callers cannot override that chrome. */
export function VitalsHeartRateInput(props: NumberInputProps) {
  return (
    <VitalsNumberInput
      {...props}
      leftSection={<VitalsHeartRateIcon />}
      rightSection={<VitalsUnitRightSection unit="bpm" />}
    />
  );
}

export function VitalsHeartRateIcon() {
  return (
    <IconHeartFilled
      size={16}
      fill="var(--mantine-color-red-9)"
      color="var(--mantine-color-red-9)"
    />
  );
}
